import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { Toaster } from "@/components/ui/sonner";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminLogin from "@/pages/admin/Login";
import Dashboard from "@/pages/admin/Dashboard";
import Products from "@/pages/admin/Products";
import ProductEditor from "@/pages/admin/ProductEditor";
import PagesList from "@/pages/admin/PagesList";
import PageEditor from "@/pages/admin/PageEditor";
import AIProductGenerator from "@/pages/admin/AIProductGenerator";
import Funnels from "@/pages/admin/Funnels";
import Offers from "@/pages/admin/Offers";
import Coupons from "@/pages/admin/Coupons";
import Affiliates from "@/pages/admin/Affiliates";
import Featured from "@/pages/admin/Featured";
import Customers from "@/pages/admin/Customers";
import Categories from "@/pages/admin/Categories";
import Orders from "@/pages/admin/Orders";
import Analytics from "@/pages/admin/Analytics";
import Settings from "@/pages/admin/Settings";
import StorefrontHome from "@/pages/storefront/Home";
import StorefrontProduct from "@/pages/storefront/ProductPage";
import StorefrontCheckout from "@/pages/storefront/Checkout";
import CheckoutSuccess from "@/pages/storefront/CheckoutSuccess";
import UpsellPage from "@/pages/storefront/UpsellPage";
import CustomerPortal from "@/pages/storefront/Portal";
import LandingPage from "@/pages/storefront/LandingPage";
import CategoryPage from "@/pages/storefront/CategoryPage";
import AffiliateDashboard from "@/pages/affiliate/AffiliateDashboard";
import { useReferralTracker } from "@/hooks/useReferral";
import api from "@/lib/api";

function AdminRoute({ children }) {
  const { admin, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen"><div className="animate-spin w-8 h-8 border-4 border-[#1A9DA5] border-t-transparent rounded-full" /></div>;
  if (!admin) return <Navigate to="/admin/login" />;
  return children;
}

function ReferralTracker({ children }) {
  useReferralTracker();
  return children;
}

function App() {
  useEffect(() => {
    api.post('/seed').catch(() => {});
  }, []);

  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <ReferralTracker>
            <Routes>
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
                <Route index element={<Dashboard />} />
                <Route path="products" element={<Products />} />
                <Route path="products/new" element={<ProductEditor />} />
                <Route path="products/:id" element={<ProductEditor />} />
                <Route path="products/generate" element={<AIProductGenerator />} />
                <Route path="pages" element={<PagesList />} />
                <Route path="pages/:id" element={<PageEditor />} />
                <Route path="funnels" element={<Funnels />} />
                <Route path="offers" element={<Offers />} />
                <Route path="coupons" element={<Coupons />} />
                <Route path="affiliates" element={<Affiliates />} />
                <Route path="featured" element={<Featured />} />
                <Route path="customers" element={<Customers />} />
                <Route path="categories" element={<Categories />} />
                <Route path="orders" element={<Orders />} />
                <Route path="analytics" element={<Analytics />} />
                <Route path="settings" element={<Settings />} />
              </Route>
              <Route path="/" element={<StorefrontHome />} />
              <Route path="/store" element={<StorefrontHome />} />
              <Route path="/guides/:slug" element={<StorefrontProduct />} />
              <Route path="/packages/:slug" element={<StorefrontProduct />} />
              <Route path="/l/:slug" element={<LandingPage />} />
              <Route path="/checkout" element={<StorefrontCheckout />} />
              <Route path="/checkout/success" element={<CheckoutSuccess />} />
              <Route path="/upsell" element={<UpsellPage />} />
              <Route path="/portal" element={<CustomerPortal />} />
              <Route path="/category/:slug" element={<CategoryPage />} />
              <Route path="/affiliate" element={<AffiliateDashboard />} />
            </Routes>
          </ReferralTracker>
        </BrowserRouter>
        <Toaster position="top-right" />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
